import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { SiteContent } from "../../server/site/siteContent";
import { api } from "./workspaceApi";
import "./SiteEditor.css";

/**
 * Раздел «Сайт» в кабинете (12.10): Сергей сам правит тексты, фото, контакты
 * и «Наши работы». Оформление и блоки задаём мы, содержимое — его.
 * Всё сохраняется кнопкой «Сохранить» (17.10: и «Наши работы» тоже) — фото
 * загружаются сразу, но на сайт работа попадает только после сохранения.
 */
type Category = "shkafy" | "stellazhi" | "komody";
type WorkCategory = Category | "other";
type Work = { id: string; title: string; caption: string; category: WorkCategory; image: string; sort: number; published: boolean; createdAt: string };
type SiteData = { content: SiteContent; defaults: SiteContent; works: Work[]; updatedAt: string | null; updatedBy: string | null; siteUrl: string | null };
/** Работа в кабинете до сохранения: у новой ещё нет id, только загруженное фото. */
type DraftWork = { key: string; id?: string; mediaId?: string; image: string; title: string; caption: string; category: WorkCategory; published: boolean };
const toDraftWork = (work: Work): DraftWork => ({
  key: work.id, id: work.id, image: work.image, title: work.title, caption: work.caption, category: work.category, published: work.published
});
/** Что уйдёт на сервер — для «есть несохранённые изменения». */
const worksSnapshot = (list: DraftWork[]) =>
  JSON.stringify(list.map((work) => [work.id ?? `new:${work.mediaId}`, work.title.trim(), work.caption.trim(), work.category, work.published]));
type Path = readonly (string | number)[];
type SectionId = "home" | "categories" | "works" | "about" | "product" | "seo";

const SECTIONS: [SectionId, string][] = [
  ["home", "Главная"], ["categories", "Разделы"], ["works", "Наши работы"],
  ["about", "О нас и контакты"], ["product", "Страница модели"], ["seo", "Для поисковиков"]
];
const CATEGORY_TITLES: Record<WorkCategory, string> = { shkafy: "Шкафы", stellazhi: "Стеллажи", komody: "Комоды", other: "Другое" };
const PAGE_TITLES: Record<keyof SiteContent["seo"], string> = {
  home: "Главная", shkafy: "Шкафы", stellazhi: "Стеллажи", komody: "Комоды", raboty: "Наши работы", "o-nas": "О нас", katalog: "Каталог"
};

const getAt = (source: unknown, path: Path): unknown =>
  path.reduce<unknown>((value, key) => (value as Record<string | number, unknown> | undefined)?.[key], source);
function setAt<T>(source: T, path: Path, value: unknown): T {
  const next = structuredClone(source) as Record<string | number, unknown>;
  let cursor = next;
  path.slice(0, -1).forEach((key) => { cursor = cursor[key] as Record<string | number, unknown>; });
  cursor[path[path.length - 1]!] = value;
  return next as T;
}
const when = (value: string) => new Date(value).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/**
 * Фото уменьшается в браузере: до 2000 px по длинной стороне, JPEG. Если всё ещё
 * больше ~700 КБ — сильнее сжимаем и уменьшаем: с телефона грузится быстро и
 * проходит через лимит сервера на размер запроса.
 */
const PHOTO_TARGET_BYTES = 700_000;
async function shrinkImage(file: File) {
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file); }
  catch { throw new Error("Этот формат фото браузер не открывает. Сохраните фото как JPG и загрузите снова"); }
  const encode = (max: number, quality: number) => {
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d")!;
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((result) => (result ? resolve(result) : reject(new Error("Не удалось обработать фото"))), "image/jpeg", quality));
  };
  let blob: Blob | null = null;
  try {
    for (const [max, quality] of [[2000, 0.85], [2000, 0.75], [1600, 0.75], [1280, 0.7]] as const) {
      blob = await encode(max, quality);
      if (blob.size <= PHOTO_TARGET_BYTES) break;
    }
  } finally { bitmap.close(); }
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Не удалось прочитать фото"));
    reader.readAsDataURL(blob!);
  });
  return { mime: "image/jpeg", data };
}
const uploadImage = async (file: File) => (await api<{ id: string; url: string }>("/admin/site/media", "POST", await shrinkImage(file)));

type Editor = {
  value: (path: Path) => unknown;
  fallback: (path: Path) => unknown;
  set: (path: Path, next: unknown) => void;
  busy: boolean;
  upload: (file: File, done: (url: string, id: string) => Promise<void> | void) => Promise<void>;
};
/** Поля — отдельные компоненты: иначе при каждой букве поле пересоздаётся и теряет курсор. */
const EditorContext = createContext<Editor | null>(null);
const useEditor = () => useContext(EditorContext)!;

function Text({ label, path, multiline, hint, rows = 3 }: { label: string; path: Path; multiline?: boolean; hint?: string; rows?: number }) {
  const { value, fallback, set } = useEditor();
  const current = String(value(path) ?? "");
  const original = String(fallback(path) ?? "");
  return <label className="se-field">
    <span>{label}{current !== original && original && <button type="button" className="se-reset" onClick={(event) => { event.preventDefault(); set(path, original); }} title={original}>как было</button>}</span>
    {multiline
      ? <textarea rows={rows} value={current} onChange={(event) => set(path, event.target.value)} />
      : <input value={current} onChange={(event) => set(path, event.target.value)} />}
    {hint && <small>{hint}</small>}
  </label>;
}
/** Список строк (пункты): по одной в строке, пустые убираются при выходе из поля. */
function Lines({ label, path, hint }: { label: string; path: Path; hint?: string }) {
  const { value, set } = useEditor();
  const items = (value(path) as string[] | undefined) ?? [];
  return <label className="se-field"><span>{label}</span>
    <textarea rows={Math.max(3, items.length + 1)} value={items.join("\n")}
      onChange={(event) => set(path, event.target.value.split("\n"))}
      onBlur={() => set(path, items.map((item) => item.trim()).filter(Boolean))} />
    {hint && <small>{hint}</small>}
  </label>;
}
function Picture({ label, path, hint }: { label: string; path: Path; hint?: string }) {
  const { value, fallback, set, busy, upload } = useEditor();
  const current = String(value(path) ?? "");
  const original = String(fallback(path) ?? "");
  return <div className="se-picture">
    <img src={current || original} alt="" loading="lazy" />
    <div>
      <strong>{label}</strong>
      {hint && <small>{hint}</small>}
      <div className="se-row">
        <label className="se-upload">Заменить фото<input type="file" accept="image/*" disabled={busy}
          onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void upload(file, (url) => set(path, url)); }} /></label>
        {original && current !== original && <button type="button" onClick={() => set(path, original)}>Фото из макета</button>}
      </div>
    </div>
  </div>;
}
function CategoryField({ label, path }: { label: string; path: Path }) {
  const { value, set } = useEditor();
  return <label className="se-field se-field--short"><span>{label}</span>
    <select value={String(value(path) ?? "stellazhi")} onChange={(event) => set(path, event.target.value)}>
      {(["shkafy", "stellazhi", "komody"] as Category[]).map((key) => <option key={key} value={key}>{CATEGORY_TITLES[key]}</option>)}
    </select></label>;
}
/** Список блоков (слайды, шаги, плитки): порядок, убрать, добавить. */
function List<T>({ path, title, render, min = 1, max = 8, add }: {
  path: Path; title: (item: T, index: number) => string; render: (index: number) => ReactNode; min?: number; max?: number; add: string;
}) {
  const { value, fallback, set } = useEditor();
  const items = (value(path) as T[] | undefined) ?? [];
  const sample = (fallback(path) as T[] | undefined)?.[0];
  const move = (from: number, to: number) => {
    const next = items.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    set(path, next);
  };
  return <div className="se-list">
    {items.map((item, index) => <div className="se-item" key={index}>
      <div className="se-item-head"><strong>{title(item, index)}</strong>
        <span className="se-row">
          <button type="button" disabled={index === 0} onClick={() => move(index, index - 1)} aria-label="Выше">↑</button>
          <button type="button" disabled={index === items.length - 1} onClick={() => move(index, index + 1)} aria-label="Ниже">↓</button>
          <button type="button" disabled={items.length <= min} onClick={() => set(path, items.filter((_, i) => i !== index))}>Убрать</button>
        </span></div>
      {render(index)}
    </div>)}
    {items.length < max && sample !== undefined && <button type="button" className="se-add" onClick={() => set(path, [...items, structuredClone(sample)])}>{add}</button>}
  </div>;
}
function Card({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  return <section className="se-card"><header><h3>{title}</h3>{lead && <p>{lead}</p>}</header>{children}</section>;
}

export function SiteEditor({ busy, run, notify, onDirtyChange }: {
  busy: boolean;
  run: (action: () => Promise<void>) => Promise<void>;
  notify: (message: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [data, setData] = useState<SiteData | null>(null);
  const [draft, setDraft] = useState<SiteContent | null>(null);
  const [section, setSection] = useState<SectionId>("home");
  const [worksDraft, setWorksDraft] = useState<DraftWork[]>([]);
  const [uploading, setUploading] = useState(0);
  const [problem, setProblem] = useState("");
  useEffect(() => { void run(async () => {
    const next = await api<SiteData>("/admin/site");
    setData(next); setDraft(next.content); setWorksDraft(next.works.map(toDraftWork));
  }); }, []);
  const worksDirty = useMemo(() => Boolean(data) && worksSnapshot(worksDraft) !== worksSnapshot(data!.works.map(toDraftWork)), [data, worksDraft]);
  const dirty = useMemo(() => Boolean(data && draft && (worksDirty || JSON.stringify(data.content) !== JSON.stringify(draft))), [data, draft, worksDirty]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);
  useEffect(() => () => onDirtyChange?.(false), []);

  if (!data || !draft) return <div className="se-loading">Загружаю сайт…</div>;

  const set = (path: Path, next: unknown) => setDraft((current) => (current ? setAt(current, path, next) : current));
  const save = () => void run(async () => {
    const kept = new Set(worksDraft.flatMap((work) => (work.id ? [work.id] : [])));
    const works = worksDirty ? {
      items: worksDraft.map((work) => ({
        ...(work.id ? { id: work.id } : { mediaId: work.mediaId }),
        title: work.title.trim(), caption: work.caption.trim(), category: work.category, published: work.published
      })),
      removed: data.works.filter((work) => !kept.has(work.id)).map((work) => work.id)
    } : undefined;
    const result = await api<{ content: SiteContent; works: Work[]; updatedAt: string | null; updatedBy: string | null }>(
      "/admin/site/content", "PUT", { content: draft, works });
    setData((current) => current && { ...current, content: result.content, works: result.works, updatedAt: result.updatedAt, updatedBy: result.updatedBy });
    setDraft(result.content);
    setWorksDraft(result.works.map(toDraftWork));
    notify("Сайт сохранён — изменения уже на страницах");
  });
  const cancel = () => { setDraft(data.content); setWorksDraft(data.works.map(toDraftWork)); };
  const upload = async (file: File, done: (url: string, id: string) => Promise<void> | void) => {
    setUploading((count) => count + 1); setProblem("");
    try { const media = await uploadImage(file); await done(media.url, media.id); }
    catch (error) { setProblem(`${file.name}: ${error instanceof Error ? error.message : "не удалось загрузить фото"}`); }
    finally { setUploading((count) => count - 1); }
  };
  const editor: Editor = { value: (path) => getAt(draft, path), fallback: (path) => getAt(data.defaults, path), set, busy, upload };

  // ——— «Наши работы»: правятся в кабинете, на сайт — кнопкой «Сохранить» ———
  const works = worksDraft;
  const addWorks = (files: FileList | null) => {
    if (!files?.length) return;
    [...files].slice(0, 20).forEach((file) => void upload(file, (url, id) => {
      setWorksDraft((list) => [{ key: `new:${id}`, mediaId: id, image: url, title: "", caption: "", category: "other", published: true }, ...list]);
    }));
  };
  const patchWork = (work: DraftWork, patch: Partial<DraftWork>) =>
    setWorksDraft((list) => list.map((item) => (item.key === work.key ? { ...item, ...patch } : item)));
  const moveWork = (index: number, to: number) => setWorksDraft((list) => {
    const next = list.slice();
    const [item] = next.splice(index, 1);
    next.splice(to, 0, item!);
    return next;
  });
  const removeWork = (work: DraftWork) => setWorksDraft((list) => list.filter((item) => item.key !== work.key));

  const body = (() => {
    switch (section) {
      case "home": return <>
        <Card title="Верхняя строка" lead="Тонкая полоса над шапкой на всех страницах."><Text label="Текст" path={["promo"]} /></Card>
        <Card title="Слайдер" lead="Первый экран главной. Фото меняются сами; подпись «На фото» ведёт в раздел.">
          <Text label="Надпись над заголовком" path={["hero", "kicker"]} />
          <Text label="Заголовок" path={["hero", "title"]} multiline rows={3} hint="Каждая строка — с новой строки, как на сайте." />
          <List<SiteContent["hero"]["slides"][number]> path={["hero", "slides"]} add="+ Добавить слайд" max={6}
            title={(slide, index) => `Слайд ${index + 1}${slide.name ? ` · ${slide.name}` : ""}`}
            render={(index) => <>
              <Picture label="Фото слайда" path={["hero", "slides", index, "image"]} hint="Горизонтальное, лучше 2000 px по ширине." />
              <div className="se-grid">
                <Text label="Что на фото" path={["hero", "slides", index, "name"]} />
                <Text label="Размеры или цена" path={["hero", "slides", index, "meta"]} />
                <CategoryField label="Ведёт в раздел" path={["hero", "slides", index, "category"]} />
              </div>
              <Text label="Описание фото для поисковиков" path={["hero", "slides", index, "alt"]} />
            </>} />
        </Card>
        <Card title="О компании одной фразой" lead="Крупный текст под слайдером. *Звёздочками* выделяется слово цветом.">
          <Text label="Подпись" path={["statement", "kicker"]} />
          <Text label="Текст" path={["statement", "text"]} multiline rows={3} />
          <List<{ value: string; label: string }> path={["statement", "facts"]} add="+ Добавить цифру" max={4} min={0}
            title={(fact, index) => `Цифра ${index + 1}${fact.value ? ` · ${fact.value}` : ""}`}
            render={(index) => <div className="se-grid">
              <Text label="Цифра" path={["statement", "facts", index, "value"]} />
              <Text label="Подпись" path={["statement", "facts", index, "label"]} />
            </div>} />
        </Card>
        <Card title="Блок «Цвет выбираете вы»" lead="Три живые 3D-модели. Цену «от …» можно оставить пустой — тогда её не видно.">
          <Text label="Заголовок" path={["colors", "title"]} multiline rows={2} />
          <Text label="Текст" path={["colors", "text"]} multiline rows={2} />
        </Card>
        <Card title="Тёмный блок с фото">
          <Picture label="Фото" path={["split", "image"]} />
          <div className="se-grid"><Text label="Подпись" path={["split", "kicker"]} /><Text label="Кнопка" path={["split", "button"]} /><CategoryField label="Кнопка ведёт в" path={["split", "category"]} /></div>
          <Text label="Заголовок" path={["split", "title"]} />
          <Text label="Текст" path={["split", "text"]} multiline />
          <Lines label="Пункты списка" path={["split", "bullets"]} hint="Каждый пункт — с новой строки." />
        </Card>
        <Card title="Две плитки с фото">
          <List<SiteContent["duo"][number]> path={["duo"]} add="+ Добавить плитку" max={2} min={0}
            title={(tile, index) => `Плитка ${index + 1}${tile.title ? ` · ${tile.title.replace(/\n/g, " ")}` : ""}`}
            render={(index) => <>
              <Picture label="Фото" path={["duo", index, "image"]} />
              <div className="se-grid"><Text label="Подпись" path={["duo", index, "kicker"]} /><Text label="Заголовок" path={["duo", index, "title"]} /><CategoryField label="Ведёт в" path={["duo", index, "category"]} /></div>
            </>} />
        </Card>
        <Card title="Как это устроено">
          <div className="se-grid"><Text label="Заголовок" path={["how", "title"]} /><Text label="Текст" path={["how", "text"]} /></div>
          <List<SiteContent["how"]["steps"][number]> path={["how", "steps"]} add="+ Добавить шаг" max={4}
            title={(step, index) => `Шаг ${index + 1} · ${step.title}`}
            render={(index) => <>
              <Picture label="Картинка шага" path={["how", "steps", index, "image"]} />
              <Text label="Заголовок" path={["how", "steps", index, "title"]} />
              <Text label="Текст" path={["how", "steps", index, "text"]} multiline rows={2} />
            </>} />
        </Card>
        <Card title="Готовые модели и подвал">
          <Text label="Заголовок блока готовых моделей" path={["models", "title"]} />
          <Text label="Фраза в подвале" path={["footer", "lead"]} />
          <Text label="Строка внизу страницы" path={["footer", "note"]} />
        </Card>
      </>;
      case "categories": return <>
        <Card title="Каталог" lead="Страница «Все модели».">
          <Text label="Заголовок" path={["catalog", "title"]} /><Text label="Текст" path={["catalog", "lead"]} multiline rows={2} />
        </Card>
        {(["shkafy", "stellazhi", "komody"] as Category[]).map((key) => <Card key={key} title={CATEGORY_TITLES[key]} lead="Модели раздела берутся из витрины кабинета.">
          <div className="se-grid"><Text label="Название" path={["categories", key, "title"]} /><Text label="Цена «от …» на главной" path={["categories", key, "priceFrom"]} hint="Например: от 52 400 ₽. Пусто — не показываем." /></div>
          <Text label="Текст под заголовком" path={["categories", key, "lead"]} multiline rows={2} />
          <div className="se-grid"><Text label="Заголовок «Соберите свой…»" path={["categories", key, "start"]} /></div>
          <div className="se-pictures">
            <Picture label="Значок в меню" path={["categories", key, "icon"]} hint="Квадратное фото." />
            <Picture label="Большая плитка" path={["categories", key, "tile"]} />
          </div>
        </Card>)}
      </>;
      case "works": return <>
        <Card title="Наши работы" lead="Новые — первыми; порядок меняется стрелками. На сайте всё появится после «Сохранить».">
          <div className="se-grid"><Text label="Заголовок" path={["works", "title"]} /><Text label="Текст" path={["works", "lead"]} /></div>
          <Text label="Пока работ нет" path={["works", "empty"]} />
          <label className="se-upload se-upload--big">{uploading ? `Загружаю фото… (${uploading})` : "+ Добавить фото работ"}
            <input type="file" accept="image/*" multiple disabled={busy} onChange={(event) => { addWorks(event.target.files); event.target.value = ""; }} /></label>
          <small className="se-muted">Можно выбрать сразу несколько фото — с телефона или компьютера. Большие фото уменьшаются автоматически.</small>
        </Card>
        {works.length ? <div className="se-works">{works.map((work, index) => <article key={work.key} className={`se-work${work.published ? "" : " is-hidden"}`}>
          <img src={work.image} alt="" loading="lazy" />
          <WorkFields work={work} onChange={(patch) => patchWork(work, patch)} />
          <div className="se-row se-work-actions">
            <button type="button" disabled={busy || index === 0} onClick={() => moveWork(index, index - 1)} aria-label="Раньше">←</button>
            <button type="button" disabled={busy || index === works.length - 1} onClick={() => moveWork(index, index + 1)} aria-label="Позже">→</button>
            <button type="button" disabled={busy} onClick={() => patchWork(work, { published: !work.published })}>{work.published ? "Скрыть" : "Показать"}</button>
            <button type="button" className="workspace-danger" disabled={busy} onClick={() => removeWork(work)}>Удалить</button>
          </div>
        </article>)}</div> : <p className="se-muted">Работ пока нет — на сайте показывается текст «{draft.works.empty}».</p>}
      </>;
      case "about": return <>
        <Card title="О нас">
          <Text label="Заголовок" path={["about", "title"]} />
          <Text label="Подзаголовок" path={["about", "lead"]} />
          <Text label="Текст" path={["about", "text"]} multiline rows={8} hint="Абзацы разделяйте пустой строкой." />
          <Picture label="Фото" path={["about", "image"]} />
        </Card>
        <Card title="Контакты" lead="Показываются в подвале всех страниц и на странице «О нас». Пустое поле — не показываем.">
          <div className="se-grid">
            <Text label="Телефон" path={["contacts", "phone"]} />
            <Text label="Почта" path={["contacts", "email"]} />
            <Text label="Telegram" path={["contacts", "telegram"]} hint="Ник или ссылка" />
            <Text label="WhatsApp" path={["contacts", "whatsapp"]} hint="Номер или ссылка" />
            <Text label="ВКонтакте" path={["contacts", "vk"]} hint="Адрес страницы" />
          </div>
          <Text label="Адрес" path={["contacts", "address"]} multiline rows={2} />
          <Text label="Часы работы" path={["contacts", "hours"]} />
          <Text label="Реквизиты" path={["contacts", "requisites"]} multiline rows={4} hint="ИП, ИНН, ОГРНИП — как в документах." />
        </Card>
      </>;
      case "product": return <>
        <Card title="Страница модели" lead="Тексты под настройкой и в раскрывающихся блоках «Характеристики».">
          <Text label="Подпись у цены" path={["product", "note"]} />
          <Text label="Сроки изготовления" path={["product", "leadTime"]} />
          <Text label="Как заказать" path={["product", "order"]} multiline />
          <Text label="Материалы" path={["product", "materials"]} multiline />
          <Text label="Доставка и сборка" path={["product", "delivery"]} multiline />
        </Card>
      </>;
      case "seo": return <>
        <Card title="Для поисковиков" lead="Заголовок вкладки браузера и описание в поиске Яндекса и Google. Пока сайт на временном адресе, поисковики его не видят.">
          {(Object.keys(PAGE_TITLES) as (keyof SiteContent["seo"])[]).map((page) => <div className="se-item" key={page}>
            <div className="se-item-head"><strong>{PAGE_TITLES[page]}</strong></div>
            <Text label="Заголовок" path={["seo", page, "title"]} />
            <Text label="Описание" path={["seo", page, "description"]} multiline rows={2} />
          </div>)}
        </Card>
      </>;
    }
  })();

  return <EditorContext.Provider value={editor}><div className="se">
    <div className="se-toolbar">
      <div className="se-status">
        {data.updatedAt ? <span>Сохранено {when(data.updatedAt)}{data.updatedBy ? ` · ${data.updatedBy}` : ""}</span> : <span>Тексты и фото — из макета</span>}
        {dirty && <span className="is-dirty">Есть несохранённые изменения</span>}
        {uploading > 0 && <span>Загружаю фото…</span>}
        {problem && <span className="is-error" role="alert">{problem}</span>}
      </div>
      <div className="se-actions">
        {data.siteUrl && <a className="se-open" href={data.siteUrl} target="_blank" rel="noopener">Открыть сайт ↗</a>}
        <button type="button" disabled={busy || !dirty} onClick={cancel}>Отменить</button>
        <button type="button" className="workspace-primary" disabled={busy || !dirty || uploading > 0} onClick={save}>Сохранить</button>
      </div>
    </div>
    <div className="se-layout">
      <nav className="se-nav" aria-label="Разделы сайта">{SECTIONS.map(([id, title]) =>
        <button key={id} type="button" aria-current={section === id ? "page" : undefined} onClick={() => setSection(id)}>
          <span>{title}</span>{id === "works" && <small>{works.length}</small>}</button>)}</nav>
      <div className="se-body">{body}</div>
    </div>
  </div></EditorContext.Provider>;
}

/** Название, подпись и раздел работы — в черновике, на сайт уходят кнопкой «Сохранить». */
function WorkFields({ work, onChange }: { work: DraftWork; onChange: (patch: Partial<DraftWork>) => void }) {
  return <div className="se-work-fields">
    <input value={work.title} placeholder="Название, например «Гардероб в спальне»" maxLength={120} aria-label="Название работы"
      onChange={(event) => onChange({ title: event.target.value })} />
    <textarea value={work.caption} placeholder="Подпись: размеры, декор, город" rows={2} maxLength={400} aria-label="Подпись работы"
      onChange={(event) => onChange({ caption: event.target.value })} />
    <select value={work.category} aria-label="Раздел" onChange={(event) => onChange({ category: event.target.value as WorkCategory })}>
      {(Object.keys(CATEGORY_TITLES) as WorkCategory[]).map((key) => <option key={key} value={key}>{CATEGORY_TITLES[key]}</option>)}
    </select>
    {!work.id && <small className="se-muted">Новая — на сайте появится после «Сохранить»</small>}
    {!work.published && <small className="se-muted">Скрыта — на сайте не видна</small>}
  </div>;
}
